import { Table, type TableColumnDefinition } from "$components/table";
import useCurrencyFormat, {
  useConvertFromPenceFactory,
} from "$routes/price-list/_lib/currencies";
import { isNotNullOrUndefined } from "$routes/price-list/_lib/predicates";
import type { PriceTariff, RevenueDepartment } from "elephant-web/gql";
import { useFormContext } from "react-hook-form";
import { useIntl } from "react-intl";
import type { AddItemsFormData } from "./use-form-schema";

interface FinalisedItemsTableProps {
  revenueDepartments?: readonly RevenueDepartment[];
  tariffs?: readonly PriceTariff[]; // only for custom items where tariffs are selectable
}

const FinalisedItemsTable = ({
  revenueDepartments,
  tariffs,
}: FinalisedItemsTableProps) => {
  const { formatMessage } = useIntl();
  const { getValues } = useFormContext<AddItemsFormData>();
  const currency = useCurrencyFormat();
  const formattedCurrency = currency?.formatToParts(Number(currency))[0].value;
  const penceToPounds = useConvertFromPenceFactory();
  const rows = Object.values(getValues())
    .filter(isNotNullOrUndefined)
    .map((r: AddItemsFormData) => ({
      ...r,
      id: r.externalReference.foreignId,
    }));

  const columns: TableColumnDefinition[] = [
    {
      field: "name",
      flex: 4,
      headerName: formatMessage({
        id: "priceList.finalisedPage.column.name",
        defaultMessage: "Item",
      }),
    },
    {
      field: "externalReference",
      flex: 2,
      headerName: formatMessage({
        id: "priceList.finalisedPage.column.origin",
        defaultMessage: "Type",
      }),
      renderCell: (value) => (
        <p style={{ textTransform: "capitalize" }}>
          {value.origin.toLowerCase()}
        </p>
      ),
    },
    {
      field: "price",
      flex: 2,
      headerName: `${formatMessage({
        id: "priceList.finalisedPage.column.price",
        defaultMessage: "Price",
      })} (${formattedCurrency})`,
      renderCell: (value) => <p>{penceToPounds(value)}</p>,
    },
    {
      field: "tariffId",
      flex: 2,
      headerName: formatMessage({
        id: "priceList.finalisedPage.column.tariff",
        defaultMessage: "Price Type",
      }),
      renderCell: (value) => {
        const tariffName = tariffs
          ? tariffs.find((r) => r.id === value)?.name
          : value;
        return <p style={{ textTransform: "capitalize" }}>{tariffName}</p>;
      },
    },
    {
      field: "revenueDepartmentId",
      flex: 2,
      headerName: formatMessage({
        id: "priceList.finalisedPage.column.revenueDepartment",
        defaultMessage: "Revenue Department",
      }),
      renderCell: (value) => {
        const revenueDepartmentName = revenueDepartments?.find(
          (r) => r.id === value,
        )?.name;
        return (
          <p style={{ textTransform: "capitalize" }}>{revenueDepartmentName}</p>
        );
      },
    },
  ];

  return <Table columns={columns} rows={rows} dense />;
};

export default FinalisedItemsTable;
